import React, { Dispatch, SetStateAction, useCallback, useEffect, useState } from "react";
import {
  FAILURES,
  ICaptureTimestamp,
  IImageFormat,
  IImageQuality,
  LivenessCaptureStep,
} from "../../../types/onboard";
import PassiveLiveness from "./passive/PassiveLiveness";
import { FaceCallback } from "@innovatrics/dot-face-auto-capture/.";
import SmileLiveness from "./active/smile/SmileLiveness";
import { SmileLivenessCallback } from "@innovatrics/dot-smile-liveness";
import { convertBlobToBase64, getUserDevice } from "../../../utils/helper";
import { IUploadLivenessRequestBody } from "../../../types/api/application.type";
import commonStyles from "../../../styles/common.module.css";
import { handleLivenessPhotoTaken } from "../../../actions/onboard";
import Failure from "../common/Failure";
import Success from "../common/Success";
import Instruction from "../common/Instruction";
import PhoneNumber from "./PhoneNumber";
import { useApiError } from "../../../provider/ApiErrorProvider";
import Error from "../../common/Error";
import { useTranslation } from "react-i18next";

interface Props {
  photoUrl: string | undefined;
  setPhotoUrl: Dispatch<SetStateAction<string | undefined>>;
  id: string | undefined;
  setImageFormat: Dispatch<SetStateAction<IImageFormat>>;
  setCaptureTimeStamp: Dispatch<SetStateAction<ICaptureTimestamp>>;
  isRetryButtonDisabled: boolean;
  setIsRetryButtonDisabled: React.Dispatch<React.SetStateAction<boolean>>;
  handleBackClick: () => void;
  handleNextOnBoardStep: () => Promise<void>;
  setImageQuality: Dispatch<SetStateAction<IImageQuality>>
}

const LivenessCapture = ({
  photoUrl,
  setPhotoUrl,
  id,
  setImageFormat,
  setCaptureTimeStamp,
  isRetryButtonDisabled,
  setIsRetryButtonDisabled,
  handleBackClick,
  handleNextOnBoardStep,
  setImageQuality
}: Props) => {
   const {setError,errorMessage} = useApiError()
  const [currentStep, setCurrentStep] = useState(1);
  const [smileIndex, setSmileIndex] = useState(0);
  const [livenessUploadedResponse, setLivenessUploadedResponse] = useState("");
  const [processing,setProcessing] = useState(false);
  const [livenessReqBody, setLivenessReqbody] =
    useState<IUploadLivenessRequestBody>({
      livenessType: "ACTIVE",
      neutral: {
        imageData: "",
        captureTimestamp: "",
        format: "JPEG",
      },
      smile: {
        imageData: "",
        captureTimestamp: "",
        format: "JPEG",
      },
    });

  const handlePassiveLivenessCapture: FaceCallback = async (
    imageData,
    content
  ) => {
    setProcessing(true)
    const imageUrl = URL.createObjectURL(imageData.image);
    setPhotoUrl(imageUrl);
    const imageQuality = imageData?.data?.detection?.sharpness * 100;
    const capturedTimeStamp = Date.now();
    const imageFormat = imageData?.image?.type?.split("/")[1]?.toUpperCase();
    setImageFormat((prev) => ({
      ...prev,
      neutral: imageFormat,
    }));
    setCaptureTimeStamp((prev) => ({
      ...prev,
      neutral: capturedTimeStamp,
    }));
    setImageQuality((prev) => ({
      ...prev,
      neutral: imageQuality,
    }));
    const base64Image = (await convertBlobToBase64(imageData.image)) as string;
    const updatedBody = {
      ...livenessReqBody,
      livenessType: "PASSIVE",
      neutral: {
        imageData: base64Image.split(",")[1],
        captureTimestamp: capturedTimeStamp.toString(),
        format: imageFormat,
      },
    };
    setLivenessReqbody(updatedBody);
    handleLivenessPhotoTaken(
      id,
      handleLivenessCaptureStep,
      updatedBody,
      setLivenessUploadedResponse,
      setError
    );
  };
  const {t} = useTranslation();
  const handleNextStep = async () => {
    setPhotoUrl(undefined);
    setCurrentStep((prevStep) => Math.min(prevStep + 1, 4));
  };

  const handleLivenessCaptureStep = async (stepNumber: number) => {
    setPhotoUrl(undefined);
    setCurrentStep(stepNumber);
  };

  const handleFailure = () => {
    if (livenessReqBody.livenessType == "PASSIVE") {
      handleLivenessCaptureStep(LivenessCaptureStep.ACTIVE_LIVENESS);
    } else {
      // Out of retries, so on rather than back — but not through the success
      // screen, which would claim a check that did not pass.
      handleLivenessCaptureStep(LivenessCaptureStep.ON_UNCONFIRMED);
    }
  };

  const handleError = useCallback((error: Error) => {
    alert(error);
  }, []);

  const handleSmileLivenessComplete: SmileLivenessCallback = async (
    imageData,
    content
  ) => {
    const [neutralImageData, smileImageData] = imageData;
    const imageUrl = URL.createObjectURL(smileImageData.image);
    const neutralImageQuality = neutralImageData?.data?.detection?.sharpness * 100;
    const smileImageQuality = smileImageData?.data?.detection?.sharpness * 100;
    const capturedTimeStamp = Date.now();
    const neutralImageFormat = neutralImageData?.image?.type
      ?.split("/")[1]
      ?.toUpperCase();
    const smileImageFormat = smileImageData?.image?.type
      ?.split("/")[1]
      ?.toUpperCase();

    setPhotoUrl(imageUrl);
    setImageFormat((prev) => ({
      ...prev,
      neutral: neutralImageFormat,
      smileLiveness: smileImageFormat,
    }));
    setCaptureTimeStamp((prev) => ({
      ...prev,
      neutral: capturedTimeStamp,
      smileLiveness: capturedTimeStamp,
    }));
    setImageQuality((prev) => ({
      ...prev,
      neutral: neutralImageQuality,
      smileLiveness: smileImageQuality,
    }));

    const neutralBase64 = await convertBlobToBase64(neutralImageData.image);
    const smileBase64 = await convertBlobToBase64(smileImageData.image);

    if (!neutralBase64 || !smileBase64) return;

    const updatedBody = {
      ...livenessReqBody,
      livenessType: "ACTIVE",
      neutral: {
        ...livenessReqBody.neutral,
        imageData: neutralBase64.split(",")[1],
        captureTimestamp: capturedTimeStamp.toString(),
        format: neutralImageFormat,
      },
      smile: {
        ...livenessReqBody.smile,
        imageData: smileBase64.split(",")[1],
        captureTimestamp: capturedTimeStamp.toString(),
        format: smileImageFormat,
      },
    };
    setLivenessReqbody(updatedBody);
    handleLivenessPhotoTaken(
      id,
      handleLivenessCaptureStep,
      updatedBody,
      setLivenessUploadedResponse,
      setError
    );
  };

    useEffect(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' }); 
    }, [currentStep]);

  const renderStep = (currentStep: LivenessCaptureStep) => {
    switch (currentStep) {
      case LivenessCaptureStep.INSTRUCTION:
        return (
          <Instruction
            header={t('liveness.title')}
            description={``}
            images={["/assets/selfie_instruction.png"]}
            tipHeader={
              <span className={commonStyles.selfieTipHeader}>
                {t('liveness.tip.header')}
              </span>
            }
            tips={[
              `${getUserDevice() == "MOBILE" ? t('liveness.tip1.mobile') : t('liveness.tip1')}`,
              `${t('liveness.tip2')}`,
              `${t('liveness.tip3')}`
            ]}
            buttonLabel={t('button.continue')}
            onClick={handleNextStep}
          />
        );
      case LivenessCaptureStep.PASSIVE_LIVENESS:
        return (
          <PassiveLiveness
            onPhotoTaken={handlePassiveLivenessCapture}
            onError={handleError}
            photoUrl={photoUrl}
            processing={processing}
          />
        );
      case LivenessCaptureStep.ACTIVE_LIVENESS:
        return (
          <SmileLiveness
            key={smileIndex}
            onComplete={handleSmileLivenessComplete}
            onError={handleError}
            onBackClick={handleBackClick}
            photoUrl={photoUrl}
            processing={processing}
            setPhotoUrl={setPhotoUrl}
            smileIndex={smileIndex}
            setSmileIndex={setSmileIndex}
            isRetryButtonDisabled={isRetryButtonDisabled}
            setIsRetryButtonDisabled={setIsRetryButtonDisabled}
          />
        );
      case LivenessCaptureStep.ON_SUCCESS:
        return (
          <Success
            header={t('liveness.success.header')}
            body={
              <span className={commonStyles.description}>
                {`${t('liveness.success.nextstep')}`}
              </span>
            }
            onClick={handleNextOnBoardStep}
            buttonLabel={t('button.continue')}
          />
        );
      /*
        Recorded, not confirmed. Stated plainly rather than dressed as either a
        success or a dead end: the applicant can still finish, and a reviewer will
        look at the selfie, so there is nothing for them to fix here.
      */
      case LivenessCaptureStep.ON_UNCONFIRMED:
        return (
          <Failure
            header={t('liveness.unconfirmed.header')}
            description={t(livenessUploadedResponse)}
            body={t('liveness.unconfirmed.tip')}
            onClick={handleNextOnBoardStep}
            buttonLabel={t('button.continue')}
            continueButtonRequired={false}
            onContinue={() => {}}
          />
        );
      case LivenessCaptureStep.UPLOAD_PHONE_NUMBER:
        return <PhoneNumber onClick={handleNextOnBoardStep} />;
      case LivenessCaptureStep.ON_FAILURE:
        return (
          <Failure
            header={t(FAILURES.LIVENESS_CAPTURE)}
            description={t(livenessUploadedResponse)}
            body={t('liveness.error.tip')}
            onClick={handleFailure}
            buttonLabel={t('button.continue')}
            continueButtonRequired={false}
            onContinue={() => {
              handleLivenessCaptureStep(LivenessCaptureStep.ON_SUCCESS);
            }}
          />
        );
    }
  };

  return <>{
    errorMessage != null ? 
    <Error errorMessage={errorMessage}/>
    :
    renderStep(currentStep)
  }</>;
};

export default LivenessCapture;
